import { INVENTORY_MODULE } from "@/api_framework/api_config";
import axios, { AxiosError } from "axios";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useConfiguration } from "../../useConfiguration";
import AxiosCurlirize from "axios-curlirize";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import { useAPIUtils } from "../../useAPIUtils";

AxiosCurlirize(axios, () => {});

export interface ICatalogDumpDetailsResponse {
  link?: string;
  [key: string]: any;
}

export const useDownloadCatalogAPI = () => {
  const configuration = useConfiguration();
  const { captureError } = useAPIUtils();
  const { sendCurl } = useCurlLogger();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const execute = useCallback(async () => {
    setIsLoading(true);
    const toastId = toast.loading("Downloading catalog...");
    try {
      const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
      const defaultToken =
        "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpYXQiOjE3ODY2OTE1MzQsImV4cCI6MTgxODIyNzUzNCwic3ViIjp7fSwidG9rZW5fdHlwZSI6ImFjY2VzcyJ9.sZJHQF5n3D9yhpczUaUEfx0iJ3jYgvhQdF2F8UlmQyE";
      const token = accessToken
        ? accessToken.startsWith("Bearer ")
          ? accessToken
          : `Bearer ${accessToken}`
        : `Bearer ${defaultToken}`;

      const response = await axios.get<ICatalogDumpDetailsResponse>(
        `${INVENTORY_MODULE?.CATALOG_DUMP_DETAILS?.baseURL}`,
        {
          headers: {
            Authorization: token,
          },
        },
      );

      const downloadUrl = response.data?.link;
      if (downloadUrl) {
        try {
          const fileResponse = await fetch(downloadUrl);
          const blob = await fileResponse.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          const fileName = downloadUrl.split("/").pop() || "catalog_details.xlsx";
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        } catch {
          const link = document.createElement("a");
          link.href = downloadUrl;
          link.setAttribute("download", "catalog_details.xlsx");
          link.setAttribute("target", "_blank");
          link.setAttribute("rel", "noopener noreferrer");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        toast.success("Catalog downloaded successfully", { id: toastId });
      } else {
        toast.error("Failed to get catalog download link", { id: toastId });
      }
    } catch (e: any) {
      if (e?.config?.["curlCommand"]) {
        sendCurl(e.config["curlCommand"]);
      }
      captureError(e as AxiosError);
      const errorMsg = e?.response?.data?.message || e?.message || "Something went wrong";
      toast.error("Failed to download catalog: " + errorMsg, { id: toastId });
    } finally {
      setIsLoading(false);
    }
  }, [configuration, captureError, sendCurl]);

  return { execute, isLoading };
};
